import React from "react";
import MuiDrawer, { DrawerProps as MuiDrawerProps } from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";

type TDrawerProps = MuiDrawerProps & {
  // Add any additional props if needed
};

const StyledDrawer = styled(MuiDrawer)(() => ({
  // Fix Safari z-index stacking context issues
  "& .MuiModal-root": {
    zIndex: "1300 !important", // Standard MUI modal z-index
  },
  "& .MuiDrawer-paper": {
    backgroundColor: "var(--tw-drawer-background-color)",
    boxShadow: "var(--tw-drawer-box-shadow)",
    // Safari-specific fixes
    zIndex: "1301 !important", // Higher than backdrop
    position: "fixed !important", // Ensure fixed positioning
    transform: "none !important", // Prevent transform conflicts on Safari
    // Webkit-specific optimizations for Safari
    WebkitTransform: "translate3d(0, 0, 0)", // Force hardware acceleration
    WebkitBackfaceVisibility: "hidden", // Prevent flickering
    // Ensure proper positioning on Safari mobile
    top: "0 !important",
    bottom: "0 !important",
    // Safari viewport fixes - ensure full height
    height: "100dvh !important",
    maxHeight: "100dvh !important",
    minHeight: "100dvh !important",
    "@supports (height: 100dvh)": {
      height: "100dvh !important", // Dynamic viewport height for Safari
      maxHeight: "100dvh !important",
      minHeight: "100dvh !important",
    },
  },
  "& .MuiModal-backdrop": {
    backgroundColor: "var(--tw-backdrop-background-color)",
    // Ensure backdrop covers all page content
    zIndex: "1200 !important", // Higher than page content, lower than drawer
    // Safari backdrop fixes
    position: "fixed !important",
    top: "0 !important",
    left: "0 !important",
    right: "0 !important",
    bottom: "0 !important",
    width: "100vw !important",
    // Safari viewport fixes - use fallback then override
    height: "100dvh !important",
    "@supports (height: 100dvh)": {
      height: "100dvh !important",
    },
  },
  // Additional Safari mobile fixes
  "@supports (-webkit-touch-callout: none)": {
    "& .MuiDrawer-paper": {
      // iOS Safari specific fixes
      WebkitOverflowScrolling: "touch",
      // Prevent zoom issues
      touchAction: "manipulation",
    },
  },
}));

const Drawer = (props: TDrawerProps) => {
  const isRight = props.anchor === "right";

  return (
    <StyledDrawer
      {...props}
      // Ensure proper z-index inheritance
      style={{
        zIndex: 1300,
        ...props.style,
      }}
      // Add Safari-specific props
      disablePortal={false} // Ensure portal rendering
      keepMounted={props.ModalProps?.keepMounted ?? true} // Keep mounted for better Safari performance
      PaperProps={{
        ...props.PaperProps,
        sx: [
          ...(Array.isArray(props.PaperProps?.sx)
            ? props.PaperProps.sx
            : props.PaperProps?.sx
            ? [props.PaperProps.sx]
            : []),
          {
            ...(isRight
              ? {
                  left: "auto !important",
                  right: "0 !important",
                }
              : {
                  left: "0 !important",
                  right: "auto !important",
                }),
          },
        ],
      }}
      ModalProps={{
        ...props.ModalProps,
        // Safari-specific modal props
        style: {
          zIndex: 1300,
          ...props.ModalProps?.style,
        },
      }}
    >
      {props.children}
    </StyledDrawer>
  );
};

export { Drawer };
